/**
 * Where the signed-in session lives in the browser.
 *
 * Sign-in is the API's own: `POST /v1/public/admin/auth/login` checks the
 * password and emails a code, and `…/login/verify` exchanges the code for an
 * access token and a refresh token. The access token is short-lived; the
 * refresh token is single use and is swapped for a new pair at
 * `…/auth/refresh` whenever the API refuses the access token (see
 * `app/api.ts`).
 *
 * This file owns three things and nothing else knows about any of them:
 *
 *   1. the token record, and which Storage it goes in;
 *   2. "remember me", which is that choice of Storage plus a remembered
 *      username to prefill;
 *   3. expiry — a session whose refresh token has run out (or an access token
 *      with no refresh token behind it) is treated as no session at all.
 *
 * `localStorage` when remembered, `sessionStorage` when not. That is the real
 * difference between the two boxes: unticked, closing the tab ends the
 * session, which is what a shared operations machine needs.
 */

import { unregisterPush } from "./push";
import { panelTimeZone } from "./timezone";

/** The token record, as stored. */
export interface AuthSession {
  /**
   * Resolved from `GET /me` once the token was accepted, so the panel can
   * answer "is this row me?" before that query refetches. Null until then.
   */
  admin_id: string | null;
  /** What was typed into the email box — used to prefill it next time. */
  username: string;
  access_token: string;
  /**
   * Epoch milliseconds. Absolute, not a duration: a duration cannot survive a
   * reload. Null when the token carries no expiry.
   */
  expires_at: number | null;
  /** Single use. Null for a session with no way to renew itself. */
  refresh_token: string | null;
  /** Epoch milliseconds past which the refresh token is refused. */
  refresh_expires_at: number | null;
}

/** What the sign-in and refresh routes answer with — the parts kept here. */
export interface IssuedTokens {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  refresh_expires_at: string;
}

const SESSION_KEY = "aa.session";
const REMEMBERED_USERNAME_KEY = "aa.remembered-username";

/**
 * Every storage access is wrapped.
 *
 * Private browsing, blocked site data and a full quota all throw on plain
 * property access, and an exception thrown while reading the session would
 * take down the router before it rendered anything. Failing to "no session" is
 * always safe; failing to a white screen is not.
 */
function read(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function write(store: Storage, key: string, value: string): void {
  try {
    store.setItem(key, value);
  } catch {
    // Non-fatal. The session lasts as long as this page does.
  }
}

function remove(store: Storage, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    // Nothing to clear.
  }
}

function parse(raw: string | null): AuthSession | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<AuthSession>;
    if (typeof value.access_token !== "string" || value.access_token === "") return null;
    return {
      admin_id: typeof value.admin_id === "string" ? value.admin_id : null,
      username: typeof value.username === "string" ? value.username : "",
      access_token: value.access_token,
      expires_at: typeof value.expires_at === "number" ? value.expires_at : null,
      refresh_token:
        typeof value.refresh_token === "string" && value.refresh_token !== ""
          ? value.refresh_token
          : null,
      refresh_expires_at:
        typeof value.refresh_expires_at === "number" ? value.refresh_expires_at : null,
    };
  } catch {
    // A half-written or hand-edited record. Treat it as signed out rather than
    // letting a malformed token reach an Authorization header.
    return null;
  }
}

/** The store the current record lives in, so updates stay where they were put. */
function storeOfSession(): Storage {
  return read(window.localStorage, SESSION_KEY) ? window.localStorage : window.sessionStorage;
}

/**
 * Whether a record can still authenticate, now or after a refresh.
 *
 * An access token past its expiry is *not* a dead session while a refresh
 * token stands behind it — deleting the record at that point would throw away
 * the one thing that can renew it.
 */
function isLive(session: AuthSession, now = Date.now()): boolean {
  if (session.refresh_token !== null) {
    return session.refresh_expires_at === null || session.refresh_expires_at > now;
  }
  return session.expires_at === null || session.expires_at > now;
}

/**
 * The current session, or null.
 *
 * Checks both stores, because "remember me" moves where the record lives and
 * a session written before the box was unticked must still be found in order
 * to be cleared. A dead record is deleted on the way past, so a stale token
 * cannot be read twice.
 */
export function readSession(): AuthSession | null {
  const stored =
    parse(read(window.localStorage, SESSION_KEY)) ??
    parse(read(window.sessionStorage, SESSION_KEY));

  if (!stored) return null;
  if (!isLive(stored)) {
    endSession();
    return null;
  }
  return stored;
}

/**
 * Whether this session was signed in with "Keep me signed in" — that is,
 * whether it outlives the browser. Push registration follows it: a session
 * that ends when the browser closes must not keep receiving pushes after.
 */
export function isRemembered(): boolean {
  return parse(read(window.localStorage, SESSION_KEY)) !== null;
}

export function isSignedIn(): boolean {
  return readSession() !== null;
}

/** The bearer token for the `Authorization` header. */
export function accessToken(): string | null {
  return readSession()?.access_token ?? null;
}

/** The token to trade at `/auth/refresh`, if this session has one. */
export function refreshToken(): string | null {
  return readSession()?.refresh_token ?? null;
}

/** Converts what the API issued into the stored record's token fields. */
export function tokensToSession(
  tokens: IssuedTokens,
): Pick<AuthSession, "access_token" | "expires_at" | "refresh_token" | "refresh_expires_at"> {
  const refreshExpiry = Date.parse(tokens.refresh_expires_at);
  return {
    access_token: tokens.access_token,
    expires_at: Date.now() + tokens.expires_in * 1000,
    refresh_token: tokens.refresh_token,
    refresh_expires_at: Number.isNaN(refreshExpiry) ? null : refreshExpiry,
  };
}

/**
 * Records a successful sign-in.
 *
 * Clears first: without that, ticking "remember me" after an unremembered
 * sign-in leaves the old record in `sessionStorage`, and `readSession` would
 * keep finding the stale one because it checks `localStorage` first only for
 * its own copy.
 */
export function startSession(session: AuthSession, remember: boolean): void {
  endSession();

  const store = remember ? window.localStorage : window.sessionStorage;
  write(store, SESSION_KEY, JSON.stringify(session));

  if (remember) {
    // The username only. A remembered password is a password in plain text on
    // disk, and the browser's own password manager already does that job
    // properly — which is what the `autoComplete` attributes on the form are
    // for.
    write(window.localStorage, REMEMBERED_USERNAME_KEY, session.username);
  } else {
    remove(window.localStorage, REMEMBERED_USERNAME_KEY);
  }
}

/**
 * Stores the pair a refresh handed back.
 *
 * Into the same store the session came from, keeping who it belongs to. The
 * refresh token is single use, so failing to keep the new one would end the
 * session at the next refresh — there is no way back to the old one.
 */
export function replaceTokens(tokens: IssuedTokens): void {
  const store = storeOfSession();
  const session = parse(read(store, SESSION_KEY));
  if (!session) return;
  write(store, SESSION_KEY, JSON.stringify({ ...session, ...tokensToSession(tokens) }));
}

/**
 * Records the admin id once `GET /me` has resolved it.
 *
 * Written back into the same store the session came from rather than a fixed
 * one, or an unremembered session would be promoted to a remembered one by the
 * act of loading the profile.
 */
export function rememberAdminId(admin_id: string): void {
  const store = storeOfSession();
  const session = parse(read(store, SESSION_KEY));
  if (!session || session.admin_id === admin_id) return;
  write(store, SESSION_KEY, JSON.stringify({ ...session, admin_id }));
}

/** Drops the session from both stores. Leaves the remembered username alone. */
export function endSession(): void {
  remove(window.localStorage, SESSION_KEY);
  remove(window.sessionStorage, SESSION_KEY);
}

/** Prefill for the email box, and the initial state of the "remember me" box. */
export function rememberedUsername(): string | null {
  return read(window.localStorage, REMEMBERED_USERNAME_KEY);
}

export function forgetUsername(): void {
  remove(window.localStorage, REMEMBERED_USERNAME_KEY);
}

export const SIGN_IN_PATH = "/sign-in";

/**
 * Where to go after signing in, from a `?from=` value.
 *
 * Only a same-app path is accepted: `//evil.example` is a path to a naive
 * check and a different origin to the browser.
 */
export function safeReturnPath(value: string | null | undefined): string {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) {
    return "/";
  }
  return value;
}

/**
 * The session is over and could not be renewed: drop it and go to sign-in.
 *
 * A full document load for the same reason as `signOut` — nothing cached for
 * this admin may survive into whoever signs in next. Where they were is
 * carried in the query string so signing back in returns them there.
 */
export function sessionExpired(): void {
  endSession();
  const { pathname, search } = window.location;
  if (pathname === SIGN_IN_PATH) return;
  window.location.replace(`${SIGN_IN_PATH}?from=${encodeURIComponent(pathname + search)}`);
}

/**
 * Signs out and leaves the app.
 *
 * Tells the API first, so the refresh token cannot be used again from a copy
 * of this browser's storage. Best effort and not awaited: `keepalive` lets the
 * request outlive the page, and a sign-out that waited on a slow server is a
 * sign-out someone walks away from before it finished.
 *
 * A full document load rather than a router navigate. RTK Query holds every
 * screen this admin looked at, and remounting the app is the only way to be
 * certain no cached row belonging to the previous session survives into the
 * next one.
 */
export function signOut(): void {
  void signOutAsync();
}

async function signOutAsync(): Promise<void> {
  const token = accessToken();
  if (token) {
    const origin = (import.meta.env.VITE_API_BASE_URL ?? "").trim().replace(/\/+$/, "");
    // First, and awaited (briefly): logout revokes the session this request
    // authenticates with, and a browser left registered keeps receiving this
    // admin's notices after they have signed out of it.
    await unregisterPush(origin, token);
    void fetch(`${origin}/v1/admin/auth/logout`, {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "x-timezone": panelTimeZone() },
      keepalive: true,
    }).catch(() => {
      // The local session is cleared either way.
    });
  }
  endSession();
  window.location.assign(SIGN_IN_PATH);
}
